/**
 * Resume Scorecard — deterministic, client-side resume quality analysis.
 *
 * Runs entirely in the browser on the in-memory ResumeJSON (no API call, no
 * cost, works on unsaved edits). The headline dimension is "Authenticity": how
 * human (vs. AI-generated) the writing reads, measured with the SAME ban-list of
 * AI-tell words/structures used by the generation prompts. Higher = more human.
 *
 * This is a guide, not a gate. It detects patterns; it does not rewrite.
 */
import type { ResumeJSON } from '../types';

export type Severity = 'high' | 'med' | 'low';

/**
 * Pointer from a finding back to the editable resume item it came from, so the
 * UI can show that item in an editable state and write changes back. Findings
 * with no single source item (completeness checks, cross-bullet density) carry
 * no path and are read-only.
 */
export type FindingPath =
  | { kind: 'summary' }
  | { kind: 'highlight'; index: number }
  | { kind: 'summary_phrase'; index: number }
  | { kind: 'bullet'; companyIndex: number; roleIndex: number; bulletIndex: number };

export interface Finding {
  id?: string;        // stable identity for resolved-tracking (always assigned in buildScorecard)
  severity: Severity;
  label: string;      // what was found, e.g. '"results-driven"'
  section: string;    // where, e.g. 'Summary' / 'Experience — Acme Inc.'
  detail?: string;    // optional snippet for context
  path?: FindingPath; // present when the finding maps to one editable item
}

export interface DimensionScore {
  key: 'authenticity' | 'quantification' | 'completeness';
  label: string;
  score: number | null;   // 0-100, or null when not applicable
  blurb: string;
  findings: Finding[];
}

export interface Scorecard {
  overall: number;
  dimensions: DimensionScore[];
}

// ── Prose collection ───────────────────────────────────────────────────────
interface Snippet { text: string; section: string; isBullet: boolean; path: FindingPath; }

const included = (v?: string) => v !== '0';

function collectProse(resume: ResumeJSON): Snippet[] {
  const out: Snippet[] = [];
  if (resume.summary?.trim()) out.push({ text: resume.summary, section: 'Summary', isBullet: false, path: { kind: 'summary' } });
  (resume.highlights || []).forEach((h, hi) => {
    if (h?.trim()) out.push({ text: h, section: 'Highlights', isBullet: true, path: { kind: 'highlight', index: hi } });
  });
  (resume.summary_phrases || []).forEach((p, pi) => {
    if (included(p.include) && p.text?.trim()) out.push({ text: p.text, section: 'Summary phrase', isBullet: false, path: { kind: 'summary_phrase', index: pi } });
  });
  (resume.experience || []).forEach((co, ci) => {
    if (!included(co.include)) return;
    const cname = co.company || 'Experience';
    (co.roles || []).forEach((role, ri) => {
      if (!included(role.include)) return;
      (role.bullets || []).forEach((b, bi) => {
        if (included(b.include) && b.text?.trim()) {
          out.push({ text: b.text, section: `Experience — ${cname}`, isBullet: true, path: { kind: 'bullet', companyIndex: ci, roleIndex: ri, bulletIndex: bi } });
        }
      });
    });
  });
  return out;
}

/** Read the current text behind a finding path from the (live) resume. */
export function getTextAtPath(resume: ResumeJSON, path: FindingPath): string {
  switch (path.kind) {
    case 'summary': return resume.summary || '';
    case 'highlight': return (resume.highlights || [])[path.index] ?? '';
    case 'summary_phrase': return (resume.summary_phrases || [])[path.index]?.text ?? '';
    case 'bullet': return (resume.experience || [])[path.companyIndex]?.roles?.[path.roleIndex]?.bullets?.[path.bulletIndex]?.text ?? '';
  }
}

/** Immutably write new text behind a finding path; returns an updated resume. */
export function setTextAtPath(resume: ResumeJSON, path: FindingPath, text: string): ResumeJSON {
  switch (path.kind) {
    case 'summary':
      return { ...resume, summary: text };
    case 'highlight':
      return { ...resume, highlights: (resume.highlights || []).map((h, i) => (i === path.index ? text : h)) };
    case 'summary_phrase':
      return { ...resume, summary_phrases: (resume.summary_phrases || []).map((p, i) => (i === path.index ? { ...p, text } : p)) };
    case 'bullet':
      return {
        ...resume,
        experience: (resume.experience || []).map((co, ci) => ci !== path.companyIndex ? co : {
          ...co,
          roles: (co.roles || []).map((role, ri) => ri !== path.roleIndex ? role : {
            ...role,
            bullets: (role.bullets || []).map((b, bi) => (bi === path.bulletIndex ? { ...b, text } : b)),
          }),
        }),
      };
  }
}

const wordCount = (s: string) => (s.trim().match(/\S+/g) || []).length;

// ── Authenticity: AI-tell detection ────────────────────────────────────────
// Weight by how strongly the term signals AI authorship. Ambiguous words that
// can be legitimate (robust, dynamic, elevate…) are 'low' so they nudge rather
// than dominate; hollow phrases that almost never appear in genuine human resume
// writing are 'high'.
const BANNED: { re: RegExp; label: string; severity: Severity }[] = [
  { re: /\bresults[-\s]driven\b/gi, label: '"results-driven"', severity: 'high' },
  { re: /\bproven track record\b/gi, label: '"proven track record"', severity: 'high' },
  { re: /\btrack record of\b/gi, label: '"track record of"', severity: 'med' },
  { re: /\bpassionate(ly)?\b/gi, label: '"passionate"', severity: 'med' },
  { re: /\bleverag(e|es|ed|ing)\b/gi, label: '"leverage"', severity: 'high' },
  { re: /\bspearhead(ed|ing|s)?\b/gi, label: '"spearheaded"', severity: 'high' },
  { re: /\bdemonstrated ability\b/gi, label: '"demonstrated ability"', severity: 'high' },
  { re: /\bdeep expertise\b/gi, label: '"deep expertise"', severity: 'med' },
  { re: /\bseamless(ly)?\b/gi, label: '"seamless"', severity: 'med' },
  { re: /\bcutting[-\s]edge\b/gi, label: '"cutting-edge"', severity: 'med' },
  { re: /\bsynerg(y|ies|istic)\b/gi, label: '"synergy"', severity: 'high' },
  { re: /\bin today'?s fast[-\s]paced\b/gi, label: '"in today\'s fast-paced…"', severity: 'high' },
  { re: /\bwheelhouse\b/gi, label: '"wheelhouse"', severity: 'med' },
  { re: /\btapestry\b/gi, label: '"tapestry"', severity: 'high' },
  { re: /\btestament to\b/gi, label: '"testament to"', severity: 'med' },
  { re: /\bnavigate the complexities\b/gi, label: '"navigate the complexities"', severity: 'high' },
  { re: /\bunleash(ing|ed|es)?\b/gi, label: '"unleash"', severity: 'med' },
  { re: /\bunlock(ing|ed|s)?\b/gi, label: '"unlock"', severity: 'low' },
  { re: /\belevat(e|ed|es|ing)\b/gi, label: '"elevate"', severity: 'low' },
  { re: /\brobust\b/gi, label: '"robust"', severity: 'low' },
  { re: /\bdynamic\b/gi, label: '"dynamic"', severity: 'low' },
];

const PENALTY: Record<Severity, number> = { high: 8, med: 5, low: 2 };

// Default rule-of-three tolerance: up to this fraction of resume items may be
// triple-lists before they count against the score. Overridable at runtime via
// the `rule_of_three_tolerance` global setting (see ScorecardModal).
export const DEFAULT_RULE_OF_THREE_TOLERANCE = 0.15;

function analyzeAuthenticity(snippets: Snippet[], tolerance: number): DimensionScore {
  const findings: Finding[] = [];
  let penalty = 0;

  // 1. Banned words / phrases
  for (const s of snippets) {
    for (const b of BANNED) {
      const m = s.text.match(b.re);
      if (m) {
        for (let i = 0; i < m.length; i++) {
          penalty += PENALTY[b.severity];
          findings.push({ severity: b.severity, label: `AI-tell word ${b.label}`, section: s.section, path: s.path });
        }
      }
    }
  }

  // 2. "not only … but also"
  for (const s of snippets) {
    const m = s.text.match(/\bnot only\b[^.!?]*\bbut also\b/gi);
    if (m) m.forEach(() => { penalty += 6; findings.push({ severity: 'med', label: '"not only … but also" construction', section: s.section, path: s.path }); });
  }

  // 3. Rule-of-three: the AI "tricolon" tell — exactly THREE SINGLE-WORD items
  //    in a serial list ("fast, reliable, and scalable" / "designed, built, and
  //    launched"). Deliberately narrow: the classic tell is short, parallel,
  //    single-word items used for rhetorical rhythm. We do NOT flag:
  //      • multi-word enumerations ("panel discussions, hackathon workshops, and
  //        conference presentations") — the single-token items in the pattern
  //        below can't match phrases with spaces;
  //      • lists of four or more items — a leading "word," before the match
  //        means it's the tail of a longer list, so it's skipped;
  //      • compound sentences whose comma-clauses aren't single words.
  //    Oxford comma optional (",? and"). A tolerance band still applies: the
  //    first `tolerance` fraction of resume items may be tricolons for FREE,
  //    only the excess is penalized. Every instance is still flagged so the
  //    user sees it — only the SCORE is forgiven. `tolerance` is a tweakable
  //    global setting (rule_of_three_tolerance).
  const TRICOLON = /([\w'-]+),\s+([\w'-]+),?\s+(?:and|or)\s+([\w'-]+)/gi;
  const ruleOfThreeFindings: Finding[] = [];
  for (const s of snippets) {
    s.text.split(/(?<=[.!?])\s+/).forEach((sent) => {
      TRICOLON.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = TRICOLON.exec(sent)) !== null) {
        // Skip the tail of a 4+ item list: a preceding "word," means an earlier item.
        if (/[\w'-]+,\s*$/.test(sent.slice(0, m.index))) continue;
        ruleOfThreeFindings.push({ severity: 'low', label: 'Rule-of-three list ("X, Y, and Z")', section: s.section, detail: m[0], path: s.path });
      }
    });
  }
  // Free allowance scales with resume size (snippet count) so the tolerance is
  // relative, not absolute — consistent with the length-invariant scoring model.
  const freeAllowance = Math.floor(tolerance * snippets.length);
  const penalizedRuleOfThree = Math.max(0, ruleOfThreeFindings.length - freeAllowance);
  penalty += penalizedRuleOfThree * 4;
  findings.push(...ruleOfThreeFindings);

  // 4. Em-dash clause splices
  for (const s of snippets) {
    const m = s.text.match(/\s[—–]\s|\s-\s/g);
    if (m && m.length) { penalty += 2 * m.length; findings.push({ severity: 'low', label: `Em-dash splice ×${m.length}`, section: s.section, path: s.path }); }
  }

  // 5. Participle-opening density across bullets ("Leading…", "Driving…")
  const bullets = snippets.filter((s) => s.isBullet);
  if (bullets.length >= 3) {
    const ing = bullets.filter((b) => /^\s*[A-Z][a-z]+ing\b/.test(b.text));
    const ratio = ing.length / bullets.length;
    if (ratio > 0.35) {
      penalty += Math.round((ratio - 0.35) * 60);
      findings.push({ severity: 'med', label: `${Math.round(ratio * 100)}% of bullets open with an "-ing" participle (monotone rhythm)`, section: 'Experience / Highlights' });
    }
  }

  // Relative scoring: score on penalty DENSITY (points per 100 words), not the
  // absolute penalty, so a longer resume isn't punished for length — only for
  // its RATE of AI-tells. Two resumes with the same density score the same
  // regardless of how many bullets they have.
  //
  // Smooth exponential decay (never a hard floor at 0): score = 100·e^(−density/K).
  // Starts at 100 with zero tells and falls off as density rises, but always
  // moves when you clean things up — unlike the old `max(0, 100 − penalty)`,
  // which pinned heavy resumes at 0 so real fixes produced no visible change.
  // K calibrated against real resumes: clean ≈ 95, repetitive ≈ 45, AI-slop ≈ 0.
  // Denominator floored at 150 words so a tiny resume with one tell doesn't crater.
  const AUTH_DECAY_SCALE = 9;
  const totalWords = snippets.reduce((n, s) => n + wordCount(s.text), 0);
  const density = (penalty / Math.max(totalWords, 150)) * 100;
  const score = totalWords === 0 ? null : Math.round(100 * Math.exp(-density / AUTH_DECAY_SCALE));

  let blurb: string;
  if (score === null) blurb = 'No written content yet to evaluate.';
  else if (score >= 90) blurb = 'Reads human and specific — very few AI tells.';
  else if (score >= 75) blurb = 'Mostly natural; a handful of AI tells worth trimming.';
  else if (score >= 50) blurb = 'Several AI-sounding patterns — worth a editing pass.';
  else blurb = 'Reads strongly AI-generated; recruiters may notice.';

  // Sort findings high→low so the worst show first; de-dupe identical lines.
  const order: Record<Severity, number> = { high: 0, med: 1, low: 2 };
  findings.sort((a, b) => order[a.severity] - order[b.severity]);

  return { key: 'authenticity', label: 'Authenticity', score, blurb, findings };
}

// ── Quantification: do bullets carry concrete numbers? ──────────────────────
const NUMBERY = /\d|%|\$|\b(one|two|three|four|five|six|seven|eight|nine|ten|dozens?|hundreds?|thousands?|millions?|billions?|k\b|m\b|x\b)\b/i;

// Default share of experience bullets that should carry a metric for full marks.
// Not every bullet can (or should) be quantified, so requiring 100% is wrong:
// hitting the target = 100/100, below it scales linearly. Overridable via the
// `quantification_target_rate` global setting (see ScorecardModal).
export const DEFAULT_QUANTIFICATION_TARGET = 0.6;

function analyzeQuantification(snippets: Snippet[], target: number): DimensionScore {
  const bullets = snippets.filter((s) => s.isBullet && s.section.startsWith('Experience'));
  if (bullets.length === 0) {
    return { key: 'quantification', label: 'Quantification', score: null, blurb: 'No experience bullets to evaluate.', findings: [] };
  }
  // Surface EVERY unquantified bullet (the panel scrolls) so the count matches
  // the score — no silent cap.
  const findings: Finding[] = [];
  let quantified = 0;
  for (const b of bullets) {
    if (NUMBERY.test(b.text)) quantified++;
    else findings.push({ severity: 'low', label: 'Bullet has no number/metric', section: b.section, detail: b.text.slice(0, 90), path: b.path });
  }
  const rate = quantified / bullets.length;
  const pct = Math.round(rate * 100);
  const targetPct = Math.round(target * 100);
  // Hit the target → 100; below it scale linearly. Ceiling at 100 (over-
  // performing is fine, but doesn't earn bonus points).
  const score = target > 0 ? Math.min(100, Math.round((rate / target) * 100)) : 100;
  // Both the number AND the wording are derived from `target`, so changing the
  // global setting keeps every reference accurate — no hardcoded "majority"/60%.
  const blurb = rate >= target
    ? `${pct}% of your experience bullets include a concrete number or metric — at or above the recommended ${targetPct}%.`
    : `${quantified} of ${bullets.length} experience bullets (${pct}%) include a concrete number or metric, below the ${targetPct}% recommendation.`;
  return { key: 'quantification', label: 'Quantification', score, blurb, findings };
}

// ── Completeness: is the core resume filled in? ─────────────────────────────
function analyzeCompleteness(resume: ResumeJSON): DimensionScore {
  const checks: { ok: boolean; label: string }[] = [
    { ok: !!resume.name?.trim(), label: 'Name' },
    { ok: (resume.contact || []).length > 0, label: 'At least one contact method' },
    { ok: !!resume.summary?.trim(), label: 'Summary' },
    {
      ok: (resume.experience || []).some((c) => included(c.include) && (c.roles || []).some((r) => (r.bullets || []).some((b) => b.text?.trim()))),
      label: 'At least one experience bullet',
    },
    { ok: (resume.skills || []).filter((s) => s.title?.trim()).length >= 3, label: 'At least 3 skills' },
    { ok: (resume.education || []).length > 0, label: 'At least one education entry' },
  ];
  const passed = checks.filter((c) => c.ok).length;
  const score = Math.round((passed / checks.length) * 100);
  const findings: Finding[] = checks.filter((c) => !c.ok).map((c) => ({ severity: 'med', label: `Missing: ${c.label}`, section: 'Resume' }));
  const blurb = `${passed} of ${checks.length} core sections present.`;
  return { key: 'completeness', label: 'Completeness', score, blurb, findings };
}

// ── Top-level ───────────────────────────────────────────────────────────────
export const WEIGHTS: Record<DimensionScore['key'], number> = { authenticity: 0.5, quantification: 0.25, completeness: 0.25 };

export function buildScorecard(resume: ResumeJSON, opts?: { ruleOfThreeTolerance?: number; quantificationTarget?: number }): Scorecard {
  const tolerance = opts?.ruleOfThreeTolerance ?? DEFAULT_RULE_OF_THREE_TOLERANCE;
  const quantTarget = opts?.quantificationTarget ?? DEFAULT_QUANTIFICATION_TARGET;
  const snippets = collectProse(resume);
  const dimensions: DimensionScore[] = [
    analyzeAuthenticity(snippets, tolerance),
    analyzeQuantification(snippets, quantTarget),
    analyzeCompleteness(resume),
  ];
  // Deterministic finding IDs (dimension + section + label + occurrence) so the
  // UI's resolved-state survives scorecard rebuilds as the resume is edited.
  for (const d of dimensions) {
    const seen: Record<string, number> = {};
    for (const f of d.findings) {
      const base = `${d.key}|${f.section}|${f.label}`;
      const n = (seen[base] = (seen[base] || 0) + 1);
      f.id = `${base}|${n}`;
    }
  }
  let wsum = 0, w = 0;
  for (const d of dimensions) {
    if (d.score === null) continue;
    wsum += d.score * WEIGHTS[d.key];
    w += WEIGHTS[d.key];
  }
  const overall = w === 0 ? 0 : Math.round(wsum / w);
  return { overall, dimensions };
}

export function scoreColor(score: number | null): { text: string; bg: string; bar: string } {
  if (score === null) return { text: 'text-gray-400', bg: 'bg-gray-50', bar: 'bg-gray-300' };
  if (score >= 85) return { text: 'text-green-700', bg: 'bg-green-50', bar: 'bg-green-500' };
  if (score >= 70) return { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-amber-500' };
  return { text: 'text-red-700', bg: 'bg-red-50', bar: 'bg-red-500' };
}
